import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { collection, doc, getDoc, getDocs, onSnapshot, setDoc, writeBatch } from 'firebase/firestore'
import { auth, db } from '../lib/firebase'
import { normalizeProfile } from '../lib/profile'
import { stripUndefined } from '../lib/objects'
import type { PublicProfile } from '../types/models'

type AuthValue = {
  user: User | null
  profile: PublicProfile | null
  loading: boolean
  error: string
  refreshProfile: () => Promise<void>
  repairProfile: () => Promise<void>
}
const AuthContext = createContext<AuthValue | null>(null)

async function migrateDraft(user: User): Promise<boolean> {
  const draftRef = doc(db, 'draftProfiles', user.uid)
  if ((await getDoc(draftRef)).exists()) return true
  const publicSnapshot = await getDoc(doc(db, 'publicProfiles', user.uid))
  if (!publicSnapshot.exists()) return false
  const profile = normalizeProfile({ ...publicSnapshot.data(), uid: user.uid, emailVerified: user.emailVerified, hasUnpublishedChanges: false })
  const links = await getDocs(collection(db, 'users', user.uid, 'links'))
  const batch = writeBatch(db)
  batch.set(draftRef, stripUndefined(profile))
  links.forEach((item) => batch.set(doc(db, 'users', user.uid, 'draftLinks', item.id), item.data()))
  await batch.commit()
  return true
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const repairProfile = useCallback(async () => {
    const active = auth.currentUser
    if (!active) return
    setLoading(true); setError('')
    try {
      let repaired = false
      for (let attempt = 0; attempt < 4 && !repaired; attempt += 1) {
        repaired = await migrateDraft(active)
        if (!repaired) await new Promise((resolve) => setTimeout(resolve, 750))
      }
      if (!repaired) setError('Your profile setup is incomplete. Please retry.')
    } catch { setError('Linkme could not load your profile. Check your connection and retry.') } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    let unsubscribeProfile: (() => void) | undefined
    let timeout: ReturnType<typeof setTimeout> | undefined
    const unsubscribeAuth = onAuthStateChanged(auth, (nextUser) => {
      unsubscribeProfile?.(); if (timeout) clearTimeout(timeout)
      setUser(nextUser); setError(''); setProfile(null)
      if (!nextUser) { setLoading(false); return }
      setLoading(true)
      let repairing = false
      timeout = setTimeout(() => { setLoading(false); setError((current) => current || 'Loading is taking longer than expected. Please retry.') }, 8000)
      unsubscribeProfile = onSnapshot(doc(db, 'draftProfiles', nextUser.uid), async (snapshot) => {
        if (snapshot.exists()) {
          if (timeout) clearTimeout(timeout)
          if (snapshot.data().emailVerified !== nextUser.emailVerified) await setDoc(snapshot.ref, { emailVerified: nextUser.emailVerified }, { merge: true })
          setProfile(normalizeProfile({ ...snapshot.data(), uid: nextUser.uid, emailVerified: nextUser.emailVerified }))
          setLoading(false); setError(''); return
        }
        if (!repairing) { repairing = true; await repairProfile(); repairing = false }
      }, () => {
        if (timeout) clearTimeout(timeout)
        setLoading(false); setError('Linkme could not access your profile. Check Firestore rules and retry.')
      })
    })
    return () => { unsubscribeAuth(); unsubscribeProfile?.(); if (timeout) clearTimeout(timeout) }
  }, [repairProfile])

  const refreshProfile = async () => {
    const active = auth.currentUser
    if (!active) return
    const snapshot = await getDoc(doc(db, 'draftProfiles', active.uid))
    if (snapshot.exists()) setProfile(normalizeProfile({ ...snapshot.data(), uid: active.uid }))
    else await repairProfile()
  }

  const value = useMemo(() => ({ user, profile, loading, error, refreshProfile, repairProfile }), [user, profile, loading, error, repairProfile])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
